import { useState } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Minus, Plus, X, Check } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

// ─── Types ────────────────────────────────────────────────────

interface BenchmarkRow {
  role: string;
  headcount: number;
  avg_salary: number;
  market_p25: number;
  market_p50: number;
  market_p75: number;
  market_p90: number;
  percentile: number;
  status: 'below' | 'at' | 'above';
  gap_to_median: number;
}

interface BenchmarkData {
  benchmarks: BenchmarkRow[];
  total_employees: number;
  employees_with_salary: number;
  generated_at: string;
}

interface DeptEquity {
  department: string;
  headcount: number;
  avg_salary: number;
  min_salary: number;
  max_salary: number;
  spread_percent: number;
  avg_tenure_years: number;
}

interface EquityData {
  department_analysis: DeptEquity[];
  equity_concerns: DeptEquity[];
  overall_avg_salary: number;
  employees_with_salary: number;
}

interface RaiseCycle {
  id: string;
  name: string;
  budget_percent: number;
  status: 'planning' | 'active' | 'completed';
  eligible_headcount: number;
  deadline: string;
  created_at: string;
}

interface RaiseCycleList {
  cycles: RaiseCycle[];
}

// ─── Helpers ─────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'above') return <TrendingUp className="w-4 h-4 text-green-400" />;
  if (status === 'below') return <TrendingDown className="w-4 h-4 text-red-400" />;
  return <Minus className="w-4 h-4 text-amber-400" />;
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    planning: 'bg-amber-500/20 text-amber-400',
    active: 'bg-green-500/20 text-green-400',
    completed: 'bg-zinc-500/20 text-zinc-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[status] ?? styles['planning']}`}>
      {status}
    </span>
  );
}

// ─── Market Benchmarks Tab ────────────────────────────────────

function MarketBenchmarksTab() {
  const { data, loading } = useApi<BenchmarkData>('people/comp-intel/benchmark');

  if (loading) return <div className="text-muted-foreground text-sm">Loading benchmarks…</div>;
  if (!data) return null;

  const maxSalary = Math.max(...data.benchmarks.map(b => b.market_p90), 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Total Employees</p>
          <p className="text-2xl font-bold text-foreground">{data.total_employees}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">With Salary Data</p>
          <p className="text-2xl font-bold text-foreground">{data.employees_with_salary}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Roles Benchmarked</p>
          <p className="text-2xl font-bold text-foreground">{data.benchmarks.length}</p>
        </div>
      </div>

      {data.benchmarks.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center text-muted-foreground text-sm">
          No salary data available. Add salary information to employee profiles to see benchmarks.
        </div>
      ) : (
        <div className="glass rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Role vs Market Comparison</h3>
          <div className="space-y-5">
            {data.benchmarks.map(row => (
              <div key={row.role}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={row.status} />
                    <span className="text-sm font-medium text-foreground">{row.role}</span>
                    <span className="text-xs text-muted-foreground">({row.headcount} people)</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-foreground">{fmt(row.avg_salary)}</span>
                    <span className="text-xs text-muted-foreground ml-1">avg · {row.percentile}th pct</span>
                  </div>
                </div>

                {/* Bar chart: p25/p50/p75/p90 bands + avg salary marker */}
                <div className="relative h-8 bg-surface-3 rounded-lg overflow-hidden">
                  {/* p25–p50 band (green) */}
                  <div
                    className="absolute top-0 h-full bg-green-500/20"
                    style={{
                      left: `${(row.market_p25 / maxSalary) * 100}%`,
                      width: `${((row.market_p50 - row.market_p25) / maxSalary) * 100}%`,
                    }}
                  />
                  {/* p50–p75 band (amber) */}
                  <div
                    className="absolute top-0 h-full bg-amber-500/20"
                    style={{
                      left: `${(row.market_p50 / maxSalary) * 100}%`,
                      width: `${((row.market_p75 - row.market_p50) / maxSalary) * 100}%`,
                    }}
                  />
                  {/* p75–p90 band (blue) */}
                  <div
                    className="absolute top-0 h-full bg-blue-500/20"
                    style={{
                      left: `${(row.market_p75 / maxSalary) * 100}%`,
                      width: `${((row.market_p90 - row.market_p75) / maxSalary) * 100}%`,
                    }}
                  />
                  {/* Avg salary marker */}
                  <div
                    className={`absolute top-1 bottom-1 w-1 rounded-full ${row.status === 'below' ? 'bg-red-400' : row.status === 'above' ? 'bg-green-400' : 'bg-amber-400'}`}
                    style={{ left: `${Math.min((row.avg_salary / maxSalary) * 100, 98)}%` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>P25: {fmt(row.market_p25)}</span>
                  <span>P50: {fmt(row.market_p50)}</span>
                  <span>P75: {fmt(row.market_p75)}</span>
                  <span>P90: {fmt(row.market_p90)}</span>
                </div>

                {row.status === 'below' && (
                  <p className="text-xs text-red-400 mt-1">
                    Gap to median: {fmt(Math.abs(row.gap_to_median))} — consider review
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Pay Equity Tab ───────────────────────────────────────────

function PayEquityTab() {
  const { data, loading } = useApi<EquityData>('people/comp-intel/equity');

  if (loading) return <div className="text-muted-foreground text-sm">Loading equity data…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Overall Avg Salary</p>
          <p className="text-2xl font-bold text-foreground">{fmt(data.overall_avg_salary)}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-xs text-muted-foreground mb-1">Equity Concerns</p>
          <p className={`text-2xl font-bold ${data.equity_concerns.length > 0 ? 'text-red-400' : 'text-green-400'}`}>
            {data.equity_concerns.length}
          </p>
          <p className="text-xs text-muted-foreground">departments</p>
        </div>
      </div>

      {data.equity_concerns.length > 0 && (
        <div className="glass rounded-xl p-4 border border-red-500/20">
          <h3 className="text-sm font-semibold text-red-400 mb-3">Equity Concerns</h3>
          <div className="space-y-2">
            {data.equity_concerns.map(dept => (
              <div key={dept.department} className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground">{dept.department}</span>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{dept.headcount} people</span>
                  <span className="text-red-400">Spread: {dept.spread_percent}%</span>
                  <span>Avg: {fmt(dept.avg_salary)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 text-xs font-medium text-muted-foreground">Department</th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground">Headcount</th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground">Avg Salary</th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground">Min</th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground">Max</th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground">Spread</th>
              <th className="text-right p-3 text-xs font-medium text-muted-foreground">Avg Tenure</th>
            </tr>
          </thead>
          <tbody>
            {data.department_analysis.map(dept => (
              <tr key={dept.department} className="border-b border-border/50 hover:bg-surface-3/30">
                <td className="p-3 font-medium text-foreground">{dept.department}</td>
                <td className="p-3 text-right text-muted-foreground">{dept.headcount}</td>
                <td className="p-3 text-right text-foreground">{fmt(dept.avg_salary)}</td>
                <td className="p-3 text-right text-muted-foreground">{fmt(dept.min_salary)}</td>
                <td className="p-3 text-right text-muted-foreground">{fmt(dept.max_salary)}</td>
                <td className={`p-3 text-right ${dept.spread_percent > 20 ? 'text-red-400' : 'text-muted-foreground'}`}>
                  {dept.spread_percent}%
                </td>
                <td className="p-3 text-right text-muted-foreground">{dept.avg_tenure_years}y</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Raise Cycles Tab ─────────────────────────────────────────

function RaiseCyclesTab() {
  const { data, loading, refetch } = useApi<RaiseCycleList>('people/comp-intel/raise-cycles');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    budget_percent: 3,
    eligibility_min_tenure_months: 6,
    deadline: '',
    notes: '',
  });

  async function createCycle() {
    setSaving(true);
    try {
      await fetch('/api/people/comp-intel/raise-cycles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setShowForm(false);
      setForm({ name: '', budget_percent: 3, eligibility_min_tenure_months: 6, deadline: '', notes: '' });
      void refetch();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-muted-foreground text-sm">Loading raise cycles…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Raise Cycles ({data?.cycles.length ?? 0})</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm"
        >
          <Plus className="w-3.5 h-3.5" /> Create Cycle
        </button>
      </div>

      {showForm && (
        <div className="glass rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground">New Raise Cycle</h4>
            <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs text-muted-foreground block mb-1">Cycle Name</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Q2 2026 Annual Review"
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Budget %</label>
              <input
                type="number"
                min="0"
                max="20"
                step="0.5"
                value={form.budget_percent}
                onChange={e => setForm(f => ({ ...f, budget_percent: parseFloat(e.target.value) || 3 }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Min Tenure (months)</label>
              <input
                type="number"
                min="0"
                value={form.eligibility_min_tenure_months}
                onChange={e => setForm(f => ({ ...f, eligibility_min_tenure_months: parseInt(e.target.value) || 6 }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Deadline</label>
              <input
                type="date"
                value={form.deadline}
                onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Notes</label>
              <input
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">
              Cancel
            </button>
            <button
              onClick={() => void createCycle()}
              disabled={saving || !form.name || !form.deadline}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 text-sm disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" /> {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {data?.cycles.length === 0 ? (
        <div className="glass rounded-xl p-8 text-center text-muted-foreground text-sm">
          No raise cycles yet. Create one to start the compensation review process.
        </div>
      ) : (
        <div className="space-y-3">
          {data?.cycles.map(cycle => (
            <div key={cycle.id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground">{cycle.name}</span>
                    <StatusBadge status={cycle.status} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Budget: {cycle.budget_percent}% · {cycle.eligible_headcount} eligible employees · Deadline: {cycle.deadline}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`/api/people/comp-intel/raise-cycles/${cycle.id}/recommendations`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-amber-400 hover:text-amber-300 px-2 py-1 rounded bg-amber-500/10 hover:bg-amber-500/20"
                  >
                    View Recommendations
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function CompensationIntelligencePage() {
  const [tab, setTab] = useState<'benchmarks' | 'equity' | 'cycles'>('benchmarks');

  const tabs = [
    { id: 'benchmarks' as const, label: 'Market Benchmarks' },
    { id: 'equity' as const, label: 'Pay Equity' },
    { id: 'cycles' as const, label: 'Raise Cycles' },
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
          <DollarSign className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">Compensation Intelligence</h1>
          <p className="text-sm text-muted-foreground">Market benchmarking, pay equity analysis, and raise cycle management</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-amber-500 text-amber-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'benchmarks' && <MarketBenchmarksTab />}
      {tab === 'equity' && <PayEquityTab />}
      {tab === 'cycles' && <RaiseCyclesTab />}
    </div>
  );
}
